import { createRouter, createWebHashHistory } from 'vue-router';
import HomeView from "@/views/HomeView.vue";
import AssociationOverview from "@/views/AssociationOverview.vue";
import NewsCenter from "@/views/NewsCenter.vue";
import PolicyRegulations from "@/views/PolicyRegulations.vue";
import MemberService from "@/views/MemberService.vue";
import ContactUs from "@/views/ContactUs.vue";
import PostDetail from "@/views/PostDetail.vue";
import AdminHome from "@/views/admin/AdminHome.vue";
import CompanyView from "@/views/admin/OrganizationView.vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import UserLogin from "@/components/UserLogin.vue";
import AboutView from "@/views/admin/AboutView.vue";
import UserLayout from "@/layout/UserLayout.vue";
import UserProfile from "@/components/UserProfile.vue";
import UserHome from "@/views/user/UserHome.vue";
import UserEmployee from "@/views/user/EmployeeView.vue";
import WorkerView from "@/views/admin/WorkerView.vue";
import WorkdayView from "@/views/user/WorkdayView.vue";
import PaymentView from "@/views/user/PaymentView.vue";
import SettlementView from "@/views/user/SettlementView.vue";
import AccountView from "@/views/admin/AccountView.vue";

const routes = [
  { path: '/', name: 'Home', component: HomeView },
  { path: '/about', name: 'AssociationOverview', component: AssociationOverview },
  { path: '/news', name: 'NewsCenter', component: NewsCenter },
  { path: '/policy', name: 'PolicyRegulations', component: PolicyRegulations },
  { path: '/service', name: 'MemberService', component: MemberService },
  { path: '/contact', name: 'ContactUs', component: ContactUs },
  { path: '/news/detail/:id', name: 'NewsDetail', component: PostDetail },
  { path: '/notification/detail/:id', name: 'NotificationDetail', component: PostDetail },
  { path: '/policy/detail/:id', name: 'PolicyDetail', component: PostDetail },
  { path: '/login', component: UserLogin },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, userType: 'admin' },
    children: [
      { path: '', redirect: 'workbench' },
      { path: 'workbench', name: 'adminWorkbench', component: () => import('@/views/admin/Workbench.vue') },
      { path: 'home', name: 'adminHome', component: AdminHome },
      { path: 'recruitment', name: 'adminRecruitment', component: () => import('@/views/admin/RecruitmentView.vue') },
      { path: 'recruitment/:id', name: 'adminJobDetail', component: () => import('@/views/user/JobDetailView.vue') },
      { path: 'company', name: 'companyView', component: CompanyView },
      { path: 'worker', name: 'workerView', component: WorkerView },
      { path: 'account', name: 'accountView', component: AccountView },
      { path: 'about', name: 'aboutView', component: AboutView },
      { path: 'website', redirect: '/admin/website/home' },
      { path: 'website/home', name: 'websiteHome', component: () => import('@/views/admin/WebsiteHome.vue') },
      { path: 'website/news', name: 'newsManager', component: () => import('@/views/admin/NewsManager.vue') },
      { path: 'website/policy', name: 'policyManager', component: () => import('@/views/admin/PolicyManager.vue') },
      { path: 'website/contact', name: 'messageManager', component: () => import('@/views/admin/MessageManager.vue') },
      { path: 'website/member-apply', name: 'memberApplyManager', component: () => import('@/views/admin/MemberApplyManager.vue') },
      { path: 'website/mediation-apply', name: 'mediationApplyManager', component: () => import('@/views/admin/MediationApplyManager.vue') },
    ],
  },
  {
    path: '/user',
    component: UserLayout,
    meta: { requiresAuth: true, userType: 'user' },
    children: [
      { path: 'home', name: 'userHome', component: UserHome },
      // { path: 'home', name: 'adminHome', component: AdminHome },
      { path: 'recruitment', name: 'RecruitmentView', component: () => import('@/views/user/RecruitmentView.vue') },
      { path: 'recruitment/:id', name: 'JobDetail', component: () => import('@/views/user/JobDetailView.vue') },
      { path: 'employee', name: 'UserEmployee', component: UserEmployee },
      { path: 'workday', name: 'workdayView', component: WorkdayView },
      { path: 'payment', name: 'paymentView', component: PaymentView },
      // { path: 'settlement', name: 'settlementView', component: SettlementView },
    ],
  },
  { path: '/profile', name: 'UserProfile', component: UserProfile, meta: { requiresAuth: true } },

  // 会员入会申请（申请人身份 apply，与管理端/用户端登录态完全隔离）
  {
    path: '/member-apply',
    name: 'MemberApply',
    component: () => import('@/views/MemberApply.vue'),
    meta: { userType: 'apply' }
  },
  {
    path: '/member-apply/my',
    name: 'MemberApplyMy',
    component: () => import('@/views/MemberApplyMy.vue'),
    meta: { userType: 'apply' }
  },

  // 调解申请（申请人身份 apply，与会员申请同一套微信扫码授权体系）
  // 只做提交页；查看走微信小程序「办事大厅 → 调解申请」与管理端
  {
    path: '/mediation-apply',
    name: 'MediationApply',
    component: () => import('@/views/MediationApply.vue'),
    meta: { userType: 'apply' }
  },

  // 在线留言（申请人身份 apply，与会员申请同一套微信扫码授权体系）
  {
    path: '/contact/my',
    name: 'ContactMy',
    component: () => import('@/views/ContactMy.vue'),
    meta: { userType: 'apply' }
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  // 申请人身份（apply）：与管理端/用户端登录态完全隔离。
  // 未完成微信扫码授权时**不能**跳账号密码登录页，而是回到会员服务页并自动弹出扫码授权弹窗。
  if (to.matched.some(record => record.meta.userType === 'apply')) {
    if (localStorage.getItem('applyAccessToken')) {
      next();
    } else {
      next({ path: '/service', query: { needAuth: '1', redirect: to.fullPath } });
    }
    return;
  }

  const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo') || '{}');
  const userType = userInfo.userType;

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!accessToken) {
      next({ path: '/login' });
    } else {
      const requiredUserType = to.meta.userType;
      if (requiredUserType && userType !== requiredUserType) {
        next({ path: '/login' });
      } else {
        next();
      }
    }
  } else {
    next();
  }
});

export default router;
